import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-6 shadow-lg">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FileText className="w-8 h-8" />
              World Trademark Register
            </h1>
            <Link to="/">
              <Button variant="outline" className="bg-white text-blue-900 hover:bg-gray-100">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto max-w-5xl px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-4xl font-bold text-blue-900 text-center mb-4">
              Terms & Conditions
            </CardTitle>
            <p className="text-center text-gray-600">
              Please read these terms carefully before using our services
            </p>
          </CardHeader>
          <CardContent className="prose max-w-none p-8 md:p-12">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Agreement to Terms</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("Client," "you," or "your") and World Trademark Register ("we," "us," or "our") regarding your use of our website and trademark registration services.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                By accessing our website, submitting a trademark application, or engaging our services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not use our services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Your continued use of our services after any changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Services Provided</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                World Trademark Register provides professional trademark registration and brand protection services, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Comprehensive trademark searches and clearance reports</li>
                <li>Preparation and filing of trademark applications with the USPTO</li>
                <li>Monitoring of trademark application status</li>
                <li>Response to USPTO office actions and objections</li>
                <li>Trademark monitoring and enforcement services</li>
                <li>Consultation on trademark strategy and protection</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our services are provided on an "as-is" and "as-available" basis. While we strive to provide accurate and timely services, we do not guarantee specific outcomes or results. Trademark registration approval is ultimately determined by the USPTO, not by our firm.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We are not a law firm and do not provide legal advice. For complex legal matters or disputes, we recommend consulting with a licensed trademark attorney.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Client Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As a client of World Trademark Register, you agree to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Accuracy of Information:</strong> Provide accurate, complete, and truthful information for your trademark application. You are responsible for the accuracy of all information submitted.</li>
                <li><strong>Timely Communication:</strong> Respond promptly to our requests for information, documentation, or clarification. Delays in communication may affect your application timeline.</li>
                <li><strong>Legal Use:</strong> Ensure that your use of the trademark does not infringe on existing rights or violate any laws or regulations.</li>
                <li><strong>Payment:</strong> Pay all fees and charges associated with our services in a timely manner as outlined in your selected service package.</li>
                <li><strong>Review:</strong> Carefully review all documents and applications before they are submitted to the USPTO. You are responsible for final approval of application content.</li>
                <li><strong>Compliance:</strong> Comply with all USPTO requirements and maintain proper use of your trademark once registered.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Fees and Payment</h2>
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Service Fees</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our service fees are clearly outlined in our pricing packages and must be paid in full before we begin work on your application. Service fees are separate from and in addition to USPTO filing fees.
              </p>
              
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Government Fees</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                USPTO filing fees are government charges required for processing your trademark application. These fees are subject to change by the USPTO and are not included in our service fees. Current USPTO fees range from $250 to $350 per class of goods or services.
              </p>
              
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Additional Charges</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Additional fees may apply for:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Office action responses (if not included in your package)</li>
                <li>Amendments or modifications to applications</li>
                <li>Additional trademark classes beyond those initially specified</li>
                <li>Expedited processing requests</li>
                <li>Appeal or litigation services</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Payment Terms</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Payment is due immediately upon ordering services. We accept major credit cards and other payment methods as specified on our website. All fees are non-refundable except as specifically provided in our refund policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Refund Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Due to the nature of our services and the immediate work that begins upon order placement, refunds are provided only under specific circumstances:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Before Filing:</strong> If you request a refund before your application has been filed with the USPTO, we will refund your service fee minus a $50 administrative processing fee.</li>
                <li><strong>After Filing:</strong> Once your application has been filed with the USPTO, no refunds will be provided for our service fees. USPTO filing fees are non-refundable under any circumstances.</li>
                <li><strong>Service Failure:</strong> If we fail to file your application due to our error, you will receive a full refund of service fees paid.</li>
                <li><strong>Duplicate Orders:</strong> If you accidentally place duplicate orders, we will refund the duplicate charge in full.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Refund requests must be submitted in writing to info@worldtrademarkregister.com within 30 days of payment. Please note that rejection by the USPTO does not qualify for a refund, as approval is not guaranteed.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">No Guarantee of Approval</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                While we maintain a high success rate, we cannot and do not guarantee that your trademark application will be approved by the USPTO. Trademark registration is ultimately at the discretion of USPTO examining attorneys who apply complex legal standards.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Factors that may affect approval include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Similarity to existing registered or pending trademarks</li>
                <li>Descriptiveness or genericness of the proposed mark</li>
                <li>Compliance with USPTO formalities and requirements</li>
                <li>Proper use of the mark in commerce</li>
                <li>Changes in USPTO policies or examination standards</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Our comprehensive search and professional filing services are designed to maximize your chances of approval, but success cannot be guaranteed. You acknowledge and accept this inherent uncertainty when engaging our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Intellectual Property Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content on our website, including text, graphics, logos, images, and software, is the property of World Trademark Register or its licensors and is protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may not:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Copy, reproduce, distribute, or create derivative works from our website content</li>
                <li>Use our trademarks, service marks, or trade names without prior written permission</li>
                <li>Remove or alter any copyright, trademark, or other proprietary notices</li>
                <li>Use automated systems (bots, scrapers) to access our website</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You retain all rights to your trademark and related materials. By using our services, you grant us a limited license to use your trademark information solely for the purpose of providing our services to you.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We understand the sensitive nature of trademark information and maintain strict confidentiality standards. We will not disclose your confidential information to third parties except:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>As required to file your trademark application with the USPTO (which becomes public record)</li>
                <li>To service providers who assist us in delivering our services (under confidentiality agreements)</li>
                <li>As required by law, court order, or government regulation</li>
                <li>With your explicit written consent</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You acknowledge that trademark applications filed with the USPTO become part of the public record and can be viewed by anyone through the USPTO's online database.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law, World Trademark Register and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Your use or inability to use our services</li>
                <li>Rejection or abandonment of your trademark application</li>
                <li>Errors, omissions, or inaccuracies in our services</li>
                <li>Delays in processing by the USPTO</li>
                <li>Loss of business, revenue, profits, or opportunities</li>
                <li>Infringement claims by third parties</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Our total liability to you for any claims arising from our services shall not exceed the amount of fees you paid to us for the specific services giving rise to the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless World Trademark Register and its affiliates from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising from: (a) your use of our services; (b) your violation of these Terms; (c) your violation of any rights of third parties; (d) any inaccurate or false information you provide; or (e) your trademark infringement of another party's rights.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Dispute Resolution</h2>
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Governing Law</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions.
              </p>
              
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Arbitration</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Any dispute, controversy, or claim arising out of or relating to these Terms or our services shall be resolved through binding arbitration in New York, NY, in accordance with the Commercial Arbitration Rules of the American Arbitration Association. The arbitrator's decision shall be final and binding.
              </p>
              
              <h3 className="text-xl font-semibold text-blue-900 mb-3">Class Action Waiver</h3>
              <p className="text-gray-700 leading-relaxed">
                You agree that any arbitration or legal proceeding shall be conducted on an individual basis and not as a class action, consolidated action, or representative action. You waive any right to participate in a class action lawsuit or class-wide arbitration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We reserve the right to terminate or suspend your access to our services immediately, without prior notice or liability, for any reason, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Breach of these Terms</li>
                <li>Fraudulent or illegal activity</li>
                <li>Failure to pay fees when due</li>
                <li>Providing false or misleading information</li>
                <li>Abusive or threatening behavior toward our staff</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                Upon termination, your right to use our services will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Severability</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Entire Agreement</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms, together with our Privacy Policy and any service-specific agreements, constitute the entire agreement between you and World Trademark Register regarding our services and supersede all prior agreements, representations, and understandings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions or concerns about these Terms and Conditions, please contact us:
              </p>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2"><strong>World Trademark Register</strong></p>
                <p className="text-gray-700 mb-2">40 Wall St 33rd Floor, Suite 3302</p>
                <p className="text-gray-700 mb-2">New York, NY 10005</p>
                <p className="text-gray-700 mb-2">Phone: +1 (123) 123-4567</p>
                <p className="text-gray-700">Email: info@worldtrademarkregister.com</p>
              </div>
            </section>

            <section>
              <p className="text-gray-600 text-sm italic">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsConditions;
